import {StockInformationType} from "../../definitions/types";
import {ArtworkEnumType, ProductEnumType, ProductStatusEnumType} from "../../definitions/enums";
import {InTransitModel} from "../view/inTransitModel";
import {BuiltToOrderModel} from "../view/builtToOrderModel";
import {OutOfStockModel} from "../view/outOfStockModel";
import {InStockModel} from "../view/inStockModel";
import {ProductStatusModel} from "../view/productStatusModel";
import {BackOrderModel} from "../view/BackOrderModel";

export class StockInformationModel {
    private readonly _isTruckShipment: boolean;
    private readonly _model: StockInformationType;

    constructor(isTruckShipment: boolean, stockInformation: StockInformationType) {
        this._isTruckShipment = isTruckShipment;
        this._model = stockInformation;
    }

    /**
     * @method actualLeadTime
     * @description Returns the actual lead time for the product.
     * @returns {number}
     */
    get actualLeadTime(): number {
        return this.isInStock ? this.leadIn : this.leadOut;
    }

    /**
     * @method artworkType
     * @description Returns the artwork type for the product.
     * @returns {ArtworkEnumType>}
     */
    get artworkType(): ArtworkEnumType {
        return this._model.artworkType ?? ArtworkEnumType.None;
    }

    /**
     * @method artworkLeadTime
     * @description Returns the artwork lead time in days.
     * @returns {number}
     */
    get artworkLeadTime(): number {
        return this._model.artworkLeadTime;
    }

    /**
     * @method isBuiltToOrder
     * @description Returns whether the product has built to order status.
     * @returns {boolean}
     */
    get isBuiltToOrder(): boolean {
        return this._model.isBuiltToOrder;
    }

    /**
     * @method isInTransit
     * @description Returns whether the product has isInTransit status.
     * @returns {boolean}
     */
    get isInTransit(): boolean {
        return this._model.isInTransit;
    }

    /**
     * @method canShipToday
     * @description Returns the stock status for the product.
     * @returns {boolean}
     */
    get canShipToday(): boolean {
        const currentDate = new Date();
        return (
            this.shipCutoffDate.getUTCFullYear() === currentDate.getUTCFullYear() &&
            this.shipCutoffDate.getUTCMonth() === currentDate.getUTCMonth() &&
            this.shipCutoffDate.getUTCDate() === currentDate.getUTCDate()
        );
    }

    /**
     * @method customArtwork
     * @description Returns the custom artwork status for the product.
     * @returns {boolean}
     */
    get customArtwork(): boolean {
        return this.artworkType !== ArtworkEnumType.None;
    }

    /**
     * @method id
     * @description Returns the id for the product.
     * @returns {number}
     */
    get id(): number {
        return this._model.id;
    }

    /**
     * @method isDiscontinued
     * @description Returns whether the product has IsDiscontinued status.
     * @returns {boolean}
     */
    get isDiscontinued(): boolean {
        return this._model.status === ProductStatusEnumType.Discontinued;
    }

    /**
     * @method inTransitDays
     * @description Returns the in transit days for the product.
     * @returns {number}
     */
    get inTransitDays(): number {
        return this._model.inTransitDays ?? 0;
    }

    /**
     * @method isAutoOffline
     * @description Returns whether the product has IsAutoOffline status.
     * @returns {boolean}
     */
    get isAutoOffline(): boolean {
        return this._model.isAutoOffline;
    }

    /**
     * @method isInStock
     * @description Returns whether the product has IsInStock status.
     * @returns {boolean}
     */
    get isInStock(): boolean {
        return this._model.isInStock;
    }


    /**
     * @method isBackOrderable
     * @description Returns whether the product has IsBackOrderable status.
     * @returns {boolean}
     */
    get isBackOrderable(): boolean {
        return this._model.isBackOrderable;
    }

    /**
     * @method isInternalProduct
     * @description Returns whether the product has isInternalProduct status.
     * @returns {boolean}
     */
    get isInternalProduct(): boolean {
        return (
            this._model.vendorCode !== undefined &&
            this._model.vendorCode !== null &&
            this._model.vendorCode.trim() !== "" &&
            this._model.vendorCode.length >= 3 &&
            this._model.vendorCode.slice(0, 3) == "INT"
        );
    }

    /**
     * @method isNewProduct
     * @description Returns whether the product has isNewProduct status.
     * @returns {boolean}
     */
    get isNewProduct(): boolean {
        return this._model.isNewProduct;
    }

    /**
     * @method leadIn
     * @description Returns the lead in for the product.
     * @returns {number | undefined}
     */
    get leadIn(): number {
        return this._model.leadIn ?? 0;
    }

    /**
     * @method leadOut
     * @description Returns the lead out for the product.
     * @returns {number | undefined}
     */
    get leadOut(): number {
        return this._model.leadOut ?? 0;
    }

    /**
     * @method minimumQuantity
     * @description Returns the minimum quantity for the product.
     * @returns {number | undefined}
     */
    get minimumQuantity(): number {
        return this._model.minQuantity ?? 1;
    }

    /**
     * @method productType
     * @description Returns the product type for the product.
     * @returns {ProductEnumType>}
     */
    get productType(): ProductEnumType {
        return this._model.productType ?? ProductEnumType.StockItem;
    }

    /**
     * @method quantityOnHand
     * @description Returns the quantity on hand for the product.
     * @returns {number | undefined}
     */
    get quantityOnHand(): number {
        return this._model.quantityOnHand ?? 0;
    }

    /**
     * @method shipCutoffDate
     * @description Returns the ship cutoff date for the product in UTC.
     * @returns {number | undefined}
     */
    get shipCutoffDate(): Date {
        return new Date(Date.parse(this._model.shipCutoffDate));
    }

    /**
     * @method shipEstimatedDate
     * @description Returns the ship estimated date for the product in UTC.
     * @returns {number | undefined}
     */
    get shipEstimatedDate(): Date {
        return new Date(Date.parse(this._model.shipEstimatedDate));
    }

    /**
     * @method shipsSameDay
     * @description Returns whether the product has ShipsSameDay status.
     * @returns {boolean}
     */
    get shipsSameDay(): boolean {
        return (
            this.leadIn === 0 &&
            this.productType === ProductEnumType.StockItem &&
            this.artworkType === ArtworkEnumType.None
        );
    }

    /**
     * @method status
     * @description Returns the product's status.
     * @returns {ProductStatusEnumType}
     */
    get status(): ProductStatusEnumType {
        return this._model.status ?? ProductStatusEnumType.Default;
    }

    /**
     * @property isTruckShipment
     * @description Returns whether that requires truck/freight shipment.
     * @returns {boolean}
     */
    get isTruckShipment(): boolean {
        return this._isTruckShipment;
    }

    get isInStockModel() {
        return InStockModel.create(this);
    }

    get isOutOfStockModel() {
        return OutOfStockModel.create(this);
    }

    get isBackOrderModel() {
        return BackOrderModel.create(this);
    }

    get IsBuiltToOrderModel() {
        return BuiltToOrderModel.create(this);
    }

    get isInTransitModel() {
        return InTransitModel.create(this);
    }

    get productStatusModel() {
        return ProductStatusModel.create(this);
    }

    /**
     * @property hasValidStatus
     * @description If the status is not null, whitespace or '_blank_', then return true.
     * @returns {boolean}
     */
    public get hasValidStatus(): boolean {
        return this._model.hasValidStatus;
    }
}